import React, { useCallback, useMemo } from 'react';

import type { AIVoiceFilterOption } from 'js/src/components/app/VoiceSelectionModal/useAIVoiceFilters';
import type {
    TypeMultiToggleItem,
    TypeMultiToggleProps,
} from 'js/src/components/common/ui/MultiToggle';
import MultiToggle from 'js/src/components/common/ui/MultiToggle';
import type {
    ChangeFilterCallback,
    SelectedFilterValueType,
} from 'js/src/data/ai-voices-and-avatars/ai-voice-modal/ai-voice-modal-types';

const ANY_OPTION: TypeMultiToggleItem = { key: 'any', value: 'Any' };

interface AIVoiceFilterMultiToggleProps {
    label: string;
    options: AIVoiceFilterOption[];
    selectedValue: SelectedFilterValueType;
    onSelect: ChangeFilterCallback;
}

const AIVoiceFilterMultiToggle: React.FC<AIVoiceFilterMultiToggleProps> = ({
    label,
    options,
    selectedValue,
    onSelect,
}) => {
    const items = useMemo(
        (): TypeMultiToggleItem[] => [
            ...options.map((option) => ({ key: option.value, value: option.display })),
            ANY_OPTION,
        ],
        [options],
    );

    const selectedItem = useMemo((): TypeMultiToggleItem['key'] | undefined => {
        const value = selectedValue ?? ANY_OPTION.key;
        const item = items.find((item) => item.key === value);
        return item ? item.key : undefined;
    }, [items, selectedValue]);

    const handleSelect = useCallback<TypeMultiToggleProps['onSelect']>(
        (value) => {
            onSelect(value === ANY_OPTION.key ? null : value);
        },
        [onSelect],
    );

    return (
        <div className="d-flex align-items-center">
            <div className="flex-grow-1 px-l5-16">{label}</div>
            <div className="px-l5-16">
                <MultiToggle items={items} selectedItem={selectedItem} onSelect={handleSelect} />
            </div>
        </div>
    );
};

export default AIVoiceFilterMultiToggle;
